// Centralized default content for the application.
// This is used as a fallback if Supabase data is missing,
// and it serves as the template when creating new content in the Admin panel.

export const defaultContent = {
  global: {
    logo: {
      text: "The Unraveled Parent ❤️",
      image: "/assets/logo.jpeg"
    },
    navLinks: [
      { name: 'Home', path: '/' },
      { name: 'About Us', path: '/about' },
      { name: 'Strategies', path: '/parenting-strategies' },
      { name: 'Workshops', path: '/workshops' },
      { name: 'Affirmations', path: '/affirmations' },
      { name: 'Self Care', path: '/self-care' },
      { name: 'Coaching', path: '/coaching' },
      { name: 'LPA', path: '/lies-price-advice' }
    ],
    headerButtonText: "Workshops",
    headerButtonLink: "/workshops",
    footerAboutText: "Founded by two passionate women, The Unraveled Parent is dedicated to bringing bold, whimsical joy and secure emotional connection to modern families.",
    contactInfo: [
      { prefix: "A.", text: "Clayton, Georgia" },
      { prefix: "E.", text: "hello@unraveledparent.com" },
      { prefix: "P.", text: "(555) 123-4567" }
    ],
    footerLinks: [
      { name: "Our Story", path: "/about" },
      { name: "Parenting Strategies", path: "/parenting-strategies" },
      { name: "Live Workshops", path: "/workshops" },
      { name: "Affirmations", path: "/affirmations" },
      { name: "Practical Self-care", path: "/self-care" },
      { name: "Private Coaching", path: "/coaching" },
      { name: "Lies, Price, & Advice", path: "/lies-price-advice" }
    ],
    socialLinks: [
      { iconName: "FiInstagram", url: "#" },
      { iconName: "FiTwitter", url: "#" },
      { iconName: "FiMail", url: "#" }
    ]
  },
  home: {
    hero: {
      miniLogoText: "Bold & Whimsical Joy",
      miniLogoImage: "/assets/logo.jpeg",
      heroImg: "/assets/hero.png",
      title: "Raising Humans That Push Every Button",
      subtitlePrefix: "Are you parenting a child who:",
      bullets: [
        "Pushes every button",
        "Can shift a room's energy",
        "Leaves you feeling exhausted"
      ],
      subtitleSuffix: "You are not alone. You are not doing it wrong. ❤️",
      parentingStrategiesButtonText: "Parenting Strategies",
      parentingStrategiesButtonLink: "/parenting-strategies",
      coachingButtonText: "1-on-1 Coaching",
      coachingButtonLink: "/coaching"
    },
    badge: {
      text: "Join 2,500+ Parents",
      subtext: "Transforming their family dynamic today."
    },
    servicesTitle: "Our Offerings",
    servicesSubtitle: "And a whole lot of love and support to you for what you do! ❤️",
    reviewsTitle: "What Parents Are Saying",
    reviewsSubtitle: "Hear from our wonderful community.",
    reviews: [
      { initials: "CG", text: "Validating, comforting, empowering, and educating. Absolutely loved it! ❤️" },
      { initials: "DN", text: "This class was simply amazing!!! It made me feel like I was not alone. I can't wait for the next class." },
      { initials: "MH", text: "This class/workshop was fantastic! I learned so much, wanna do it again! Thank you for sharing your wisdom, your guidance, your stories!" },
      { initials: "LM", text: "The class was so healing, helpful and hilarious. I thoroughly recommend it!" },
      { initials: "HW", text: "Thank you for a wonderful day where I felt supported, understood and empowered. Great workshop!" }
    ],
    services: [
      { iconName: "FiBookOpen", title: "Parenting Strategies", desc: "Learn to track triggers, regulate emotions, and find behavioral patterns.", link: "/parenting-strategies" },
      { iconName: "FiVideo", title: "Workshops", desc: "Interactive sessions to learn and grow together.", link: "/workshops" },
      { iconName: "FiSmile", title: "Affirmations", desc: "Change negative self-talk, celebrate wins, and focus on daily affirmations.", link: "/affirmations" },
      { iconName: "FiHeart", title: "Practical Self-care", desc: "Actionable ideas to recharge and sustain your well-being.", link: "/self-care" },
      { iconName: "FiUsers", title: "Individual Consultations", desc: "Not to be confused with therapy, but support and encouragement.", link: "/coaching" },
      { iconName: "FiPlayCircle", title: "Lies, Price, and Terrible Advice", desc: "Navigate terrible advice, differentiate lies from truths, and realize your priceless value.", link: "/lies-price-advice" }
    ]
  },
  about: {
    title: "About Us",
    missionParagraphs: [
      "The Unraveled Parent exists to change how society understands and supports families raising challenging children. We center the lived reality of parents whose children are emotionally intense, neurodivergent, oppositional, highly sensitive, explosive, or creatively nonconforming—children who don’t fit conventional molds and require more from the systems around them.",
      "We provide practical, evidence-informed parenting strategies; grounded guidance for navigating schools and IEP processes; and clear, compassionate support for the daily complexities these families face. We affirm the full emotional landscape of parenting under constant vigilance—chronic exhaustion, decision fatigue, relational strain, and the quiet grief for the “easier” path once imagined—while rejecting shame and isolation.",
      "Our work is rooted in empowerment. We equip parents to advocate effectively, regulate their own nervous systems, strengthen their relationships, and reclaim sustainable self-care without guilt. We foster a community where honesty replaces judgment, resilience replaces burnout, and parents are seen not as failing—but as adapting, learning, and leading in extraordinary circumstances.",
      "By amplifying these voices and experiences, The Unraveled Parent seeks to shift cultural narratives, influence systems of care and education, and ensure that no parent navigating complexity feels alone, dismissed, or unseen. ❤️"
    ],
    whoWeAreTitle: "Who we are",
    aboutUsImg: "/assets/about-us.jpeg",
    bios: [
      {
        name: "Kelly Carlisle",
        credentials: "LAPC",
        paragraphs: [
          "Kelly is a Licensed Associate Professional Counselor who brings a wealth of experience working with “challenging Children”. Kelly has been a children’s ministry director, youth director, summer camp and afterschool program director, DFCS case worker and most importantly a mom of three biological children and three step children, one of which is a “challenging child”.",
          "Kelly currently splits her time between Family Art Therapy Center and the Power House where she works with children and families experiencing trauma from sexual abuse and/or domestic violence."
        ],
        quote: ""
      },
      {
        name: "Beth Black",
        credentials: "LPC, ATR-BC, CPCS",
        paragraphs: [
          "Beth is a Licensed Professional Counselor, a Board Certified Art Therapist and Certified Professional Counselor Supervisor. But more importantly she is a parent of an adult child who suffers from Bi-Polar Disorder, ADHD, Cognitive Challenges and is on the Spectrum. She has more than 25 years navigating her personal journey of raising an adoptive child with multiple challenges, as well as helping families with their own challenging children.",
          "I also have to admit, that most of my experience comes from what I did not do right. Or what I thought I did not do right. Either way, I hold on to the fact that I did the best I could given the information I had at the time and the resources that were available. What I do know is I tried. I felt like a failure at times, I didn’t know how to advocate because I gave my power to the “experts” and still spend many hours worrying about my adult son. What I will no longer carry is the shame and embarrassment of how I think I am being perceived…I invite anyone questioning my “parenting style” to come spend a weekend at my house.",
          "Beth is the founder and owner of Family Art Therapy Center, PC, established in 1997. She has extensive experience working with families experiencing trauma. Beth and husband have been foster parents and are adoptive parents."
        ],
        quote: "“I have to admit, there is no one magic wand, and I am constantly learning”."
      }
    ]
  },
  "parenting-strategies": {
    title: "Parenting Strategies",
    subtitle: "Move away from reaction and step into intentional connection. Our strategies help you understand the 'why' behind the behavior.",
    overline: "From Chaos to Connection",
    strategies: [
      { id: 1, title: "Track Triggers", iconName: "FiTarget", desc: "Identify the patterns before the storm. Learn what sets off challenging behaviors so you can anticipate and proactively support your child.", iconBg: "bg-blue-100", iconBorder: "border-blue-200", iconColor: "text-blue-600" },
      { id: 2, title: "Regulate Emotions", iconName: "FiActivity", desc: "A dysregulated adult cannot regulate a dysregulated child. Discover physical and mental techniques to maintain your center in the heat of the moment.", iconBg: "bg-yellow-100", iconBorder: "border-yellow-200", iconColor: "text-yellow-600" },
      { id: 3, title: "Find Behavioral Patterns", iconName: "FiSearch", desc: "Look beneath the surface. Behavior is communication. We'll help you decode what your child is trying to say when words fail them.", iconBg: "bg-red-100", iconBorder: "border-red-200", iconColor: "text-brand-rose" }
    ],
    groundingExamples: [
      "Anchor breath: Inhale 4, exhale 6. Keep voice low.",
      "Feet-on-floor reset: Press feet down, bend knees.",
      "One-phrase grounding: 'Stay calm' / 'They’re having a hard time.'",
      "Micro body release: Unclench jaw, drop shoulders.",
      "Narrow focus: 'Just keep my voice steady for 2 minutes.'",
      "Sensory reset: Cold water on hands.",
      "After-moment discharge: Shake hands, step away 30 sec."
    ],
    voodooDollVenting: {
      title: "Voodoo-Doll Venting",
      description: "Write what you can't say out loud. Keep it private. Then tear it out if needed.",
      quote: "\"One string I'd pull today...\""
    },
    bodyMappingPain: {
      title: "Body-Mapping Pain",
      description: "Describe where stress lives (tight jaw, knot in stomach, heavy shoulders...)",
      questions: [
        "What emotion is in your neck/shoulders?",
        "What does your stomach need to say?"
      ]
    },
    wantToLearnMore: {
      title: "Want to Learn A Lot More?",
      description: "Join one of our interactive workshops. Let's work together to build a secure emotional connection and bring bold, whimsical joy back to your family. Sign up to learn a lot more!",
      buttonText: "Sign up for a Workshop",
      buttonLink: "/workshops"
    }
  },
  workshops: {
    title: "Workshops & Courses",
    subtitle: "Deepen your understanding through our expert-led workshops.",
    workshops: [
      {
        id: 1,
        title: "The Unraveled Parent",
        date: "May 8, 2026",
        type: "Live Event",
        price: 49,
        desc: "A comprehensive deep dive into maintaining your sanity and emotional regulation, exploring daily logs, weekly taunt tracking, and surviving the chaos of parenthood.",
        instructor: "Beth Black LPC, ATR-BC, CPCS and Kelly Carlisle LAPC",
        image: "/The_Unraveled_Parent_Workshop.png",
        soldOut: true,
        completed: false
      },
      {
        id: 2,
        title: "The Teen Connection Blueprint",
        date: "Self-Paced Course",
        time: "4 Hours of Content",
        type: "On-Demand Video",
        price: 129,
        desc: "Rebuild trust and open communication channels with your teenager. This comprehensive course covers boundaries, screen time, and emotional regulation.",
        instructor: "Dr. Mark Lewis",
        image: "https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&q=80",
        locked: true,
        completed: false
      },
      {
        id: 3,
        title: "Co-Parenting Harmony Workshop",
        date: "Next Session: Nov 02, 2023",
        time: "1:00 PM - 4:00 PM PST",
        type: "Live Virtual Event",
        price: 89,
        desc: "For couples looking to get on the same page. Address differing parenting styles and learn how to present a united, supportive front for your children.",
        instructor: "Dr. Jenkins & Dr. Lewis",
        image: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?auto=format&fit=crop&q=80",
        locked: true,
        completed: false
      }
    ]
  },
  affirmations: {
    title: "Affirmations",
    subtitle: "Parenting is hard enough without your inner critic joining in. It's time to speak to yourself with the same compassion you want to offer your child.",
    overline: "Nurture Your Inner Voice",
    affirmations: [
      { id: 1, title: "Change Negative Self-Talk", iconName: "FiSun", desc: "Replace 'I'm messing this up' with 'I am learning alongside my child'. Your internal dialogue sets the tone for your external reactions.", iconBg: "bg-blue-100", iconBorder: "border-blue-200", iconColor: "text-blue-600" },
      { id: 2, title: "Celebrate Wins", iconName: "FiStar", desc: "Did you stay calm during a meltdown? Did you repair after yelling? Acknowledge the small victories—they build the foundation for lasting change.", iconBg: "bg-yellow-100", iconBorder: "border-yellow-200", iconColor: "text-yellow-600" },
      { id: 3, title: "Daily Affirmations", iconName: "FiHeart", desc: "Start your day rooted in truth. 'Challenging behavior is not a parenting failure.' Anchor yourself before the chaos begins.", iconBg: "bg-red-100", iconBorder: "border-red-200", iconColor: "text-brand-rose" }
    ],
    affirmationExamples: [
      "I am the exact right parent for my child.",
      "My worth is not defined by my child's behavior.",
      "I am doing my best, and my best is enough.",
      "It is safe to ask for help.",
      "Repairing a rupture builds resilience.",
      "I am breaking generational cycles.",
      "My child is having a hard time, not giving me a hard time."
    ],
    wantToLearnMore: {
      title: "Want to Learn A Lot More?",
      description: "Transform your mindset and practice these affirmations in real-time. Join our workshops to connect, learn a lot more, and grow together.",
      buttonText: "Sign up for a Workshop",
      buttonLink: "/workshops"
    }
  },
  "selfcare": {
    title: "Practical Self-Care",
    subtitle: "Actionable ideas to recharge and sustain your well-being. Self-care isn't a luxury; it's the foundation of effective parenting.",
    overline: "Prioritize Your Peace",
    selfCareStrategies: [
      { id: 1, title: "Micro-Breaks", iconName: "FiCoffee", desc: "Take 5 minutes before getting out of the car or hide in the pantry. Small moments of quiet are essential for resetting your nervous system.", iconBg: "bg-blue-100", iconBorder: "border-blue-200", iconColor: "text-blue-600" },
      { id: 2, title: "Sensory Shift", iconName: "FiHeadphones", desc: "Use noise-canceling headphones for 10 minutes or splash cold water on your face. Disrupt the sensory overload before it leads to burnout.", iconBg: "bg-yellow-100", iconBorder: "border-yellow-200", iconColor: "text-yellow-600" },
      { id: 3, title: "Breath Anchors", iconName: "FiWind", desc: "Inhale for 4, exhale for 6. Focus on your breathing to move from a state of reaction into a state of grounded intention.", iconBg: "bg-red-100", iconBorder: "border-red-200", iconColor: "text-brand-rose" }
    ],
    selfCareExamples: [
      "Take 10 min before getting out of the car",
      "5 slow breaths",
      "Step into another room",
      "Cold water on face",
      "Music to shift mood",
      "Drink water + eat a meal",
      "Get outside 5 min",
      "Write to unload",
      "Text a friend who gets it",
      "Trade childcare time",
      "Say 'no' to one thing",
      "Go to bed 20 min earlier"
    ],
    wantToLearnMore: {
      title: "Want to Learn A Lot More?",
      description: "Join one of our workshops to learn a lot more about practical, realistic ways to weave self-care into the chaos of daily life.",
      buttonText: "Sign up for a Workshop",
      buttonLink: "/workshops"
    }
  },
  coaching: {
    title: "1-on-1 Parent Coaching",
    subtitle: "Sometimes general advice isn't enough. Get personalized, strategic support tailored specifically to your family's unique dynamics and challenges.",
    expectationsHeading: "What to Expect",
    expectations: [
      "Comprehensive initial intake to understand your family history and current struggles.",
      "Actionable, step-by-step strategies rather than just 'talk therapy'.",
      "Text/email support between sessions for real-time problem solving.",
      "Focus on parental emotional regulation as the foundation for child behavior."
    ],
    discoveryCall: {
      title: "Discovery Call",
      priceText: "Free",
      description: "A 20-minute consultation to discuss your needs and see if our coaching style is the right fit for your family.",
      buttonText: "Schedule Free Call",
      buttonLink: "tel:5551234567"
    },
    singleFocusSession: {
      title: "Single Focus Session",
      priceText: "$175",
      description: "A 60-minute deep dive into a specific issue (e.g., sleep, sibling rivalry, school refusal) with a post-session action plan.",
      buttonText: "Book Session",
      buttonLink: "mailto:hello@unraveledparent.com"
    }
  },
  "lies-price-advice": {
    title: "Lies, Price, & Terrible Advice",
    subtitle: "Unpack the unsolicited opinions, reject the parental guilt, and discover the true value of the hard work you put in every single day.",
    overline: "Reclaim Your Confidence",
    sections: [
      { id: 2, title: "Lies vs. Truths", iconName: "FiCheckCircle", desc: "Lie: 'If I were better, this wouldn't happen.' Truth: 'Challenging behavior ≠ parenting failure.' We break down the toxic myths holding you back.", iconBg: "bg-blue-100", iconBorder: "border-blue-200", iconColor: "text-blue-600" },
      { id: 3, title: "Your Priceless Value", iconName: "FiDollarSign", desc: "Carpool driver, chef, emotional regulator, peacekeeper. Calculate the hidden labor of parenting and recognize the immense, priceless worth of your role.", iconBg: "bg-yellow-100", iconBorder: "border-yellow-200", iconColor: "text-yellow-600" },
      { id: 1, title: "Navigating Terrible Advice", iconName: "FiAlertOctagon", desc: "'Just be stricter.' 'It's a phase.' 'My kid never did that.' Learn to filter out the noise and trust your intuition as the expert on your child.", iconBg: "bg-red-100", iconBorder: "border-red-200", iconColor: "text-brand-rose" }
    ],
    adviceExamples: [
      "“Just be stricter”",
      "“It’s a phase”",
      "“My kid never did that”",
      "“Have you tried a reward chart?”",
      "“Pray about it”",
      "“Take away screens”"
    ],
    liesAndTruths: [
      { lie: "If I were better, this wouldn't happen.", truth: "Challenging behavior ≠ parenting failure" },
      { lie: "Other families don't deal with this.", truth: "Many struggle silently. You're not alone." },
      { lie: "I should stay calm every time.", truth: "Repair matters more than perfection." }
    ],
    pricelessRoles: [
      { role: "Carpool driver", rate: "$20/hr" },
      { role: "Cook/meal planner", rate: "$25/hr" },
      { role: "Emotional regulator", rate: "$50/hr" },
      { role: "Your Worth", rate: "Priceless" }
    ],
    terribleAdviceTitle: "Terrible Advice Hall of Fame",
    wantToLearnMore: {
      title: "Want to Learn A Lot More?",
      description: "Stop listening to the terrible advice and start implementing strategies that actually work for neurodivergent and strong-willed kids. Sign up for a workshop and learn a lot more!",
      buttonText: "Sign up for a Workshop",
      buttonLink: "/workshops"
    }
  }
};
